import React, { useState, useEffect } from 'react';
import { Search, User, Mail, X, Users } from 'lucide-react';
import axios from 'axios';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const EmailListSelector = ({ value, onChange, multiple = true, required = false }) => {
  const { getProtectedTenantCode } = useTenantProtection();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'customers'

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Initialize selected emails from value
    if (value) {
      if (Array.isArray(value)) {
        setSelectedEmails(value);
      } else if (typeof value === 'string') {
        // Handle comma-separated string
        const emails = value.split(',').map(email => email.trim()).filter(Boolean);
        setSelectedEmails(emails);
      }
    }
  }, [value]);

  useEffect(() => {
    // Filter items based on search term and active tab
    const items = activeTab === 'users' ? users : customers;
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const filtered = items.filter(item =>
        item.email.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term)
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [searchTerm, users, customers, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      
      // Load users
      console.log('[EmailListSelector] Loading users...');
      let usersResponse = await axios.get('/api/users', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      console.log('[EmailListSelector] Users response:', usersResponse.data);
      
      // Handle redirect response for admin users
      if (usersResponse.data.redirect) {
        console.log('[EmailListSelector] Following redirect to:', usersResponse.data.redirect);
        usersResponse = await axios.get(usersResponse.data.redirect, {
          headers: { 'X-Tenant-Code': tenantCode },
        });
        console.log('[EmailListSelector] Redirected users response:', usersResponse.data);
      }
      
      // Handle both array and object with users property
      const usersArray = Array.isArray(usersResponse.data) 
        ? usersResponse.data 
        : (usersResponse.data.users || []);
      
      console.log('[EmailListSelector] Users array:', usersArray);
      
      const usersData = usersArray.map(u => ({
        id: u.UserID || u.userId,
        email: u.Email || u.email,
        name: `${u.FirstName || u.firstName || ''} ${u.LastName || u.lastName || ''}`.trim() || u.username || 'Unknown',
        type: 'user'
      }));
      console.log('[EmailListSelector] Parsed users:', usersData.length, 'users');
      setUsers(usersData);
      
      // Load customers
      console.log('[EmailListSelector] Loading customers...');
      const customersResponse = await axios.get('/api/customers', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      console.log('[EmailListSelector] Customers response raw:', customersResponse.data);
      console.log('[EmailListSelector] Customers response type:', typeof customersResponse.data);
      console.log('[EmailListSelector] Is array?', Array.isArray(customersResponse.data));
      
      // Handle different response formats
      let customersArray = [];
      if (Array.isArray(customersResponse.data)) {
        customersArray = customersResponse.data;
      } else if (customersResponse.data && customersResponse.data.customers) {
        customersArray = customersResponse.data.customers;
      } else if (customersResponse.data && customersResponse.data.data) {
        customersArray = customersResponse.data.data;
      } else if (customersResponse.data && typeof customersResponse.data === 'object') {
        console.warn('[EmailListSelector] Unexpected customers response format:', customersResponse.data);
        customersArray = [];
      }
      
      console.log('[EmailListSelector] Customers array count:', customersArray.length);
      console.log('[EmailListSelector] First customer sample:', customersArray[0]);
      
      const customersData = customersArray
        .map(c => {
          // Handle two types of customers:
          // 1. Person type: email field from Persons table
          // 2. Company type: companyemail field from Companies table
          
          let email = null;
          let name = '';
          
          if (c.CustomerType === 'Person') {
            // Person customer - check person email fields
            email = c.email || c.Email || c.PersonEmail || c.personEmail || c.person_email;
            name = `${c.FirstName || c.PersonFirstName || ''} ${c.LastName || c.PersonLastName || ''}`.trim();
            if (!name) name = c.PersonName || c.fullName || 'Unknown Person';
          } else if (c.CustomerType === 'Company') {
            // Company customer - check company email fields
            email = c.companyemail || c.companyEmail || c.CompanyEmail || c.company_email;
            name = c.CompanyName || c.companyName || c.company_name || 'Unknown Company';
          }
          
          // If still no email, try generic Email field as fallback
          if (!email) {
            email = c.Email || c.email;
          }
          
          if (!email) {
            console.log('[EmailListSelector] Customer without email:', {
              id: c.CustomerID,
              type: c.CustomerType,
              fields: Object.keys(c).filter(k => k.toLowerCase().includes('email'))
            });
            return null;
          }
          
          return {
            id: c.CustomerID,
            email: email,
            name: name || 'Unknown',
            type: 'customer',
            customerType: c.CustomerType
          };
        })
        .filter(c => c !== null); // Remove customers without emails
      
      console.log('[EmailListSelector] Parsed customers:', customersData.length, 'customers');
      console.log('[EmailListSelector] Sample parsed customer:', customersData[0]);
      setCustomers(customersData);
      
      // Set initial filtered items
      setFilteredItems(usersData);
    } catch (error) {
      console.error('[EmailListSelector] Failed to load data:', error);
      console.error('[EmailListSelector] Error message:', error.message);
      console.error('[EmailListSelector] Error response:', error.response?.data);
      console.error('[EmailListSelector] Error status:', error.response?.status);
      console.error('[EmailListSelector] Full error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSelect = (email) => {
    let newSelection;
    
    if (multiple) {
      // Multiple selection
      if (selectedEmails.includes(email)) {
        newSelection = selectedEmails.filter(e => e !== email);
      } else {
        newSelection = [...selectedEmails, email];
      }
    } else {
      // Single selection
      newSelection = [email];
      setShowDropdown(false);
    }
    
    setSelectedEmails(newSelection);
    if (onChange) {
      onChange(newSelection);
    }
  };

  const handleRemoveEmail = (email, e) => {
    e.stopPropagation();
    const newSelection = selectedEmails.filter(e => e !== email);
    setSelectedEmails(newSelection);
    if (onChange) {
      onChange(newSelection);
    }
  };

  const getItemDetails = (email) => {
    return [...users, ...customers].find(item => item.email === email);
  };

  return (
    <div className="space-y-2">
      {/* Selected Emails Display */}
      {selectedEmails.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedEmails.map(email => {
            const item = getItemDetails(email);
            return (
              <div
                key={email}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm"
              >
                <Mail className="h-3 w-3" />
                <span className="font-medium">
                  {item ? item.name : email}
                </span>
                <span className="text-xs opacity-75">
                  {email}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveEmail(email, e)}
                  className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Dropdown */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder={`Search emails... ${selectedEmails.length > 0 ? `(${selectedEmails.length} selected)` : ''}`}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />

            {/* Dropdown Menu */}
            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setActiveTab('users')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'users'
                      ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <User className="h-4 w-4 inline mr-1" />
                  Users ({users.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('customers')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'customers'
                      ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Users className="h-4 w-4 inline mr-1" />
                  Customers ({customers.length})
                </button>
              </div>

              {/* Items List */}
              <div className="overflow-y-auto max-h-60">
                {loading ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                    Loading...
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                    {searchTerm ? 'No items found matching your search' : `No ${activeTab} with email addresses`}
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredItems.map(item => {
                      const isSelected = selectedEmails.includes(item.email);
                      return (
                        <button
                          key={`${item.type}-${item.id}`}
                          type="button"
                          onClick={() => handleEmailSelect(item.email)}
                          className={`
                            w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                            ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                          `}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                                ${isSelected 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                }
                              `}>
                                {item.type === 'user' ? <User className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                  {item.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center truncate">
                                  <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                  {item.email}
                                </div>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="text-blue-500 dark:text-blue-400 ml-2">✓</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {multiple 
          ? 'Click to select multiple email addresses from users or customers.'
          : 'Click to select an email address.'
        }
      </p>

      {/* Manual Input Option */}
      <details className="mt-2">
        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
          Or enter email addresses manually
        </summary>
        <div className="mt-2">
          <textarea
            value={Array.isArray(selectedEmails) ? selectedEmails.join(', ') : selectedEmails}
            onChange={(e) => {
              const emails = e.target.value.split(',').map(email => email.trim()).filter(Boolean);
              setSelectedEmails(emails);
              if (onChange) {
                onChange(emails);
              }
            }}
            rows={2}
            placeholder="email1@example.com, email2@example.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter email addresses separated by commas
          </p>
        </div>
      </details>
    </div>
  );
};

export default EmailListSelector;
